// src/index.tsx
// MUST be the very first import: captures the native window.fetch before
// Amplitude Session Replay (or CleverTap, etc.) can wrap it, so the streaming
// chat requests aren't broken by a recorder cloning/consuming the stream body.
import "./utils/nativeFetch";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import "./index.css";

import { init } from "@amplitude/analytics-browser";
import { Experiment } from "@amplitude/experiment-js-client";
import { createRoot } from "react-dom/client";

import "./clevertapClient";
import { register as registerServiceWorker } from "./utils/serviceWorkerRegistration";
// DEV-ONLY QA shortcut for the foreign-visitor experience (tarot, guest chat,
// Google login, USD pricing). AuthContext already honours
// localStorage.auraForceCountry; this just lets you set it from the URL so a
// fresh private window needs no console:
//     http://localhost:3000/?country=US     -> behave as a US visitor
//     http://localhost:3000/?country=clear  -> back to real geo
// Stripped from production builds by the NODE_ENV guard.
if (process.env.NODE_ENV === "development") {
  try {
    const c = new URLSearchParams(window.location.search).get("country");
    if (c === "clear") {
      localStorage.removeItem("auraForceCountry");
    } else if (c) {
      localStorage.setItem("auraForceCountry", c.toUpperCase());
    }
  } catch {
    /* storage blocked — fall back to the console one-liner */
  }
}

// Create a global amplitudeApp container
export const amplitudeApp: any = {};

console.log("[CleverTap] Runtime env check", {
  accountId: process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID,
  region: process.env.REACT_APP_CLEVERTAP_REGION,
});

// ---- 1. ANALYTICS INIT ----
const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;

if (AMPLITUDE_API_KEY) {
  init(AMPLITUDE_API_KEY, undefined, {
    // `autocapture.elementInteractions` is the piece Amplitude Heatmaps need —
    // it emits the click/tap/interaction events heatmaps are plotted from
    // (Session Replay only supplies the page snapshots). Same keys as the prior
    // `defaultTracking` config, plus elementInteractions. Recording is still
    // gated to non-Indian users at the Session Replay layer, unchanged.
    autocapture: {
      pageViews: false,
      sessions: true,
      formInteractions: true,
      fileDownloads: true,
      elementInteractions: true,
    },
  });

  console.log("[Amplitude] Analytics initialized");
} else {
  console.error("Missing REACT_APP_AMPLITUDE_API_KEY");
}

// ---- 2. EXPERIMENT INIT (USING THE SAME PROJECT API KEY) ----
if (AMPLITUDE_API_KEY) {
  amplitudeApp.experiment =
    Experiment.initializeWithAmplitudeAnalytics(AMPLITUDE_API_KEY);

  console.log(
    "[Amplitude] Experiment initialized with Project API Key (FLAG CLIENT READY)",
  );
} else {
  console.error("Amplitude Experiment Key missing");
}

const container = document.getElementById("root") as HTMLElement;

// ALWAYS client-render — never hydrate. react-snap prerenders static HTML into
// #root for SEO/crawlers, but that snapshot (logged-out, no live data, no auth)
// does NOT match a real user's dynamic render, so hydrateRoot() mismatches and
// can break the live UI. createRoot().render() cleanly re-renders over the
// static HTML: crawlers still get the prerendered content, users get a correct
// fresh render. (The old `hasChildNodes() ? hydrate : render` branch is exactly
// what crashed the site once prerender started producing non-empty #root.)
// The boundary belongs HERE, not only around the routes. Because we render
// rather than hydrate, React owns #root from the first commit: the prerendered
// HTML is gone, so an uncaught error unmounts the whole tree and leaves the
// bare body background - a blank purple sheet with no navbar and no error
// screen. App's inner <ErrorBoundary> only wraps <Routes>, so everything above
// it (AuthProvider, PricingProvider, Navbar, BottomNav, the analytics widgets)
// had no net at all. Nested boundaries still work as before: the inner one
// catches route errors and keeps the chrome, this one catches the rest.
createRoot(container).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>,
);

// ---- 3. SERVICE WORKER REGISTRATION FOR CLEVERTAP WEB PUSH ----
// Only register in production. In development a service worker caches the
// bundle and serves stale CSS/JS, which blocks every change from showing up.
if (process.env.NODE_ENV === "production") {
  registerServiceWorker({
    onSuccess: () => {
      console.log("[CleverTap] Service worker registered successfully");
    },
    onUpdate: (registration) => {
      console.log("[CleverTap] New service worker version available");
      // Optionally notify user about update
    },
    onError: (error) => {
      console.error("[CleverTap] Service worker registration failed:", error);
    },
  });
} else if ("serviceWorker" in navigator) {
  // Dev: proactively kill any previously-registered SW so it stops serving
  // cached assets.
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
}
